import { CommonModule } from '@angular/common';
import { Component, ViewChild, signal, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { JobPosition } from '../../../models/jobPosition';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { LookupTableComponent, ColumnConfig } from '../../shared/lookup-table/lookup-table.component';
import { LookupTableSearchFilter } from '../../../models/lookupTableSearchFilter';
import { LookupTableSearchBoxComponent } from '../../shared/lookup-table-search-box/lookup-table-search-box.component';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { LutService } from '../../../services/lut.service';
import { HotToastService } from '@ngxpert/hot-toast';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-job-position-list',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    LookupTableComponent,
    LookupTableSearchBoxComponent,
    NgxSpinnerModule
  ],
  templateUrl: './job-position-list.component.html',
  styleUrl: './job-position-list.component.scss'
})
export class JobPositionListComponent implements OnInit {
  @ViewChild(LookupTableComponent) lookupTable!: LookupTableComponent;

  jobPositions = signal<JobPosition[]>([]);
  isSearchVisible: boolean = false;
  isExporting: boolean = false;
  matchedItems: JobPosition[] | undefined = undefined;
  rightItem: JobPosition | undefined = undefined;

  // Pagination state
  currentPage: number = 1;
  pageSize: number = 25;
  totalCount: number = 0;
  sortBy: string = 'name';
  ascending: boolean = true;

  // Search filter state
  searchFilter: LookupTableSearchFilter = {};

  jobPositionsColumns: ColumnConfig[] = [
    { key: 'name', header: 'Name', type: 'text', sortable: true, editable: true }
  ];

  constructor(
    private confirmationDialogService: ConfirmationDialogService,
    private spinnerService: NgxSpinnerService,
    private lutService: LutService,
    private toast: HotToastService
  ) {}

  ngOnInit(): void {
    this.loadJobPositions();
  }

  loadJobPositions(): void {
    this.spinnerService.show();

    // Check if any search filters are active
    const hasSearchFilters = this.searchFilter.name;

    if (hasSearchFilters) {
      // Use search endpoint when filters are active
      const searchRequest = {
        pageNumber: this.currentPage,
        pageSize: this.pageSize,
        sortBy: this.sortBy,
        ascending: this.ascending,
        ...this.searchFilter
      };

      this.lutService.searchJobPositions(searchRequest).subscribe({
        next: (response) => {
          this.jobPositions.set(response.data);
          this.totalCount = response.totalCount;
          this.spinnerService.hide();
        },
        error: (error) => {
          console.error('Error loading job positions:', error);
          this.spinnerService.hide();
          this.toast.error(`<strong>Failed to load Job Positions:</strong></br></br> ${Helpers.parseApiError(error)}`);
        }
      });
    } else {
      // Use regular paginated endpoint when no filters are active
      this.lutService.getJobPositionsPaginated(
        this.currentPage,
        this.pageSize,
        this.sortBy,
        this.ascending
      ).subscribe({
        next: (response) => {
          this.jobPositions.set(response.data);
          this.totalCount = response.totalCount;
          this.spinnerService.hide();
        },
        error: (error) => {
          console.error('Error loading job positions:', error);
          this.spinnerService.hide();
          this.toast.error(`<strong>Failed to load Job Positions:</strong></br></br> ${Helpers.parseApiError(error)}`);
        }
      });
    }
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex + 1; // MatPaginator uses 0-based index
    this.pageSize = event.pageSize;
    this.loadJobPositions();
  }

  onSortChange(sort: Sort): void {
    // If sort direction is cleared (empty string), reset to default sort
    if (!sort.direction) {
      this.sortBy = 'name';
      this.ascending = true;
    } else {
      this.sortBy = sort.active;
      this.ascending = sort.direction === 'asc';
    }

    this.loadJobPositions();
  }

  onCreateJobPosition(): void {
    this.lookupTable.startAdd();
  }

  toggleSearch(): void {
    this.isSearchVisible = !this.isSearchVisible;
  }

  onExportToCsv(): void {
    if (this.isExporting) return;
    this.isExporting = true;
    this.spinnerService.show();

    this.lutService.exportJobPositionsToCsv({
      name: this.searchFilter.name
    }).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `job_positions_export_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        window.URL.revokeObjectURL(url);
        this.spinnerService.hide();
        this.isExporting = false;
        this.toast.success('Job positions exported successfully');
      },
      error: (error) => {
        console.error('Error exporting job positions:', error);
        this.toast.error(`<strong>Failed to export Job Positions:</strong></br></br> ${Helpers.parseApiError(error)}`);
        this.spinnerService.hide();
        this.isExporting = false;
      }
    });
  }

  getAppliedFiltersCount(): number {
    const { exactMatch, ...actualFilters } = this.searchFilter;
    return Object.values(actualFilters).filter(value => value !== undefined && value !== null && value !== '').length;
  }

  onSearchFilterChange(searchFilter: LookupTableSearchFilter): void {
    this.searchFilter = searchFilter;
    this.currentPage = 1; // Reset to first page when searching

    // Reset the paginator to first page visually
    if (this.lookupTable?.['paginator']) {
      this.lookupTable['paginator'].pageIndex = 0;
    }

    this.loadJobPositions();
  }

  onItemSelected(jobPosition: JobPosition): void {
  }

  onReadyToMatch(event: {matchedItems?: JobPosition[], rightItem?: JobPosition}): void {
    this.matchedItems = event.matchedItems;
    this.rightItem = event.rightItem;
  }

  onEditJobPosition(jobPosition: JobPosition): void {
  }

  onDeleteJobPosition(jobPosition: JobPosition): void {
    this.confirmationDialogService.openDeleteConfirmationDialog(jobPosition.name)
      .subscribe(result => {
        if (result) {
          const originalJobPositions = this.jobPositions();

          this.jobPositions.update(jobPositions => jobPositions.filter(jp => jp.id !== jobPosition.id));

          this.spinnerService.show();
          this.lutService.deleteJobPosition(jobPosition.id).subscribe({
            next: () => {
              this.spinnerService.hide();
              this.toast.success('Job position deleted successfully');
              // Reload current page
              this.loadJobPositions();
            },
            error: (error) => {
              console.error('Error deleting job position:', error);
              this.spinnerService.hide();
              this.jobPositions.set(originalJobPositions);
              this.toast.error(`<strong>Failed to delete Job Position:</strong></br></br> ${Helpers.parseApiError(error)}`);
            }
          });
        }
      });
  }

  onItemSave(event: {item: JobPosition, newName: string} | {item: JobPosition, newValue: any, columnKey: string}): void {
    const originalItem = { ...event.item };

    let updatedItem: JobPosition;
    if ('newName' in event) {
      updatedItem = { ...event.item, name: event.newName };
    } else {
      updatedItem = { ...event.item, [event.columnKey]: event.newValue };
    }

    this.jobPositions.update(jobPositions =>
      jobPositions.map(jp => jp.id === event.item.id ? updatedItem : jp)
    );

    this.spinnerService.show();
    this.lutService.updateJobPosition(event.item.id, updatedItem).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Job position updated successfully');
        this.jobPositions.update(jobPositions =>
          jobPositions.map(jp => jp.id === event.item.id ? response : jp)
        );
      },
      error: (error) => {
        console.error('Error updating job position:', error);
        this.spinnerService.hide();
        this.jobPositions.update(jobPositions =>
          jobPositions.map(jp => jp.id === event.item.id ? originalItem : jp)
        );
        this.toast.error(`<strong>Failed to update Job Position:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onItemAdd(event: {[key: string]: any}): void {
    const tempId = crypto.randomUUID();
    const newJobPosition: JobPosition = {
      id: tempId,
      name: event['name'],
      systemReserved: false
    };

    this.jobPositions.update(jobPositions => [newJobPosition, ...jobPositions]);

    this.spinnerService.show();
    this.lutService.createJobPosition({ name: event['name'], systemReserved: false }).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Job position created successfully');
        this.jobPositions.update(jobPositions =>
          jobPositions.map(jp => jp.id === tempId ? response : jp)
        );
      },
      error: (error) => {
        console.error('Error creating job position:', error);
        this.spinnerService.hide();
        this.jobPositions.update(jobPositions =>
          jobPositions.filter(jp => jp.id !== tempId)
        );
        this.toast.error(`<strong>Failed to create Job Position:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onJobPositionCorrected(): void {
    if (!this.rightItem || !this.matchedItems || this.matchedItems.length === 0) {
      console.error('Missing required data for merge operation');
      this.toast.error('Please select items to merge');
      return;
    }

    const bulkMergeRequest = {
      leftIds: this.matchedItems.map(item => item.id),
      rightId: this.rightItem.id
    };

    const itemsToMerge = this.matchedItems.map(item => item.name).join(', ');
    this.confirmationDialogService.openConfirmationDialog(
      'Confirm Merge',
      `Are you sure you want to merge "${itemsToMerge}" into "${this.rightItem.name}"? This action cannot be undone.`,
      'Merge',
      'Cancel',
      'merge_type',
      'clear'
    ).subscribe(result => {
      if (result && this.rightItem) {
        const originalJobPositions = this.jobPositions();

        this.jobPositions.update(jobPositions =>
          jobPositions.filter(jp => !bulkMergeRequest.leftIds.includes(jp.id))
        );

        this.rightItem = undefined;
        this.matchedItems = undefined;

        this.spinnerService.show();
        this.lutService.mergeJobPositionsBulk(bulkMergeRequest).subscribe({
          next: () => {
            this.spinnerService.hide();
            this.toast.success('Job positions merged successfully');
            this.lookupTable.clearMatchingState();
            this.loadJobPositions();
          },
          error: (error) => {
            console.error('Error merging job positions:', error);
            this.spinnerService.hide();
            this.jobPositions.set(originalJobPositions);
            this.rightItem = originalJobPositions.find(jp => jp.id === bulkMergeRequest.rightId);
            this.matchedItems = originalJobPositions.filter(jp => bulkMergeRequest.leftIds.includes(jp.id));
            this.toast.error(`<strong>Failed to merge Job Positions:</strong></br></br> ${Helpers.parseApiError(error)}`);
          }
        });
      }
    });
  }
}
